import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { getUserInfo } from '../utils/tokenManager';
import { createNewProductContent, saveProductContentInfo, addItinerarySchedule, saveItineraryContent, getItineraryType, saveItineraryMeal, saveHotelItineraryEdits, getItineraryDetail, getFlightScheduleByFlightNo, saveFlightItinerary, getTourProductResourceList, getTourRatePlanList02 } from '../services/apiService';
import { parseItineraryResponse } from '../utils/itineraryParser';
import { simplifyHtmlTable } from '../utils/htmlTableSimplifier';

const SmartItineraryRegisterModal = forwardRef(({
  open,
  onClose,
  productNo,
  contentTypes = [],
  defaultItineraryContentTypeName = '일정소개',
  onCompleted,
  mode = 'modal',
  onTabChange,
}, ref) => {
  const PRICE_STOCK_CONTENT_TYPE_CODE = '__PRICE_STOCK__';
  const [tab, setTab] = useState('editor'); // editor | review

  // Notify parent when tab changes
  useEffect(() => {
    onTabChange && onTabChange(tab);
  }, [tab, onTabChange]);
  const [loading, setLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [infoBlocks, setInfoBlocks] = useState([]); // [{id,title,bodyHtml,rawHtml, contentTypeCode}]
  const [cssText, setCssText] = useState('');
  const [itineraryHtml, setItineraryHtml] = useState('');
  const [headLinksHtml, setHeadLinksHtml] = useState('');
  const [structuredItinerary, setStructuredItinerary] = useState(null); // subData.itinerary
  const [previewMode, setPreviewMode] = useState('json'); // html | json
  const [jsonLineRoles, setJsonLineRoles] = useState({}); // key -> 'title' | 'content' | 'ignore'
  const [error, setError] = useState('');
  const iframeRef = useRef(null);
  const [seReady, setSeReady] = useState(false);
  const seGetResolverRef = useRef(null);

  const existingContentByTitle = useMemo(() => {
    const map = {};
    (contentTypes || []).forEach((row) => {
      let arr = [];
      try {
        arr = JSON.parse(row.컨텐츠 || '[]');
      } catch { }
      if (Array.isArray(arr)) {
        arr.forEach((it) => {
          if (!it) return;
          const tKey = ['컨텐츠명', '콘텐츠명', '항목명', '제목', '타이틀', '이름', '명', 'title', 'name', 'text'].find(
            (k) => Object.prototype.hasOwnProperty.call(it, k)
          );
          const title = tKey ? String(it[tKey] || '').trim() : '';
          if (!title) return;
          if (!map[title]) map[title] = it;
        });
      }
    });
    return map;
  }, [contentTypes]);

  const htmlToMultilineText = useCallback((html) => {
    const s = String(html || '');
    const inner = s.replace(/^\s*<p[^>]*>/i, '').replace(/<\/p>\s*$/i, '');
    return inner.replace(/<br\s*\/?>(\r?\n)?/gi, '\n');
  }, []);

  const stripHtmlToText = useCallback((input) => {
    const s = String(input ?? '');
    if (!s) return '';
    try {
      let normalized = s;
      normalized = normalized.replace(/<br\s*\/?\s*>/gi, '\n');
      normalized = normalized.replace(/<\/(p|div|tr|li|h[1-6])\s*>/gi, '\n');
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${normalized}</div>`, 'text/html');
      const text = (doc.body && doc.body.textContent) ? doc.body.textContent : '';
      return String(text)
        .replace(/[\u00A0]/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/\s{2,}/g, ' ')
        .trim();
    } catch {
      return s
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/[\u00A0]/g, ' ')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/\s{2,}/g, ' ')
        .trim();
    }
  }, []);

  const normalizeFlightNo = useCallback((value) => {
    return String(value || '')
      .toUpperCase()
      .replace(/[\s\-]+/g, '')
      .trim();
  }, []);

  const extractFlightNo = useCallback((value) => {
    const raw = String(value || '').toUpperCase();
    if (!raw) return '';
    const m = raw.match(/([A-Z0-9]{2})\s*[-]?\s*(\d{3,4})/);
    if (!m) return '';
    return normalizeFlightNo(`${m[1]}${m[2]}`);
  }, [normalizeFlightNo]);

  const isFlightNoLike = useCallback((value) => {
    const v = normalizeFlightNo(value);
    if (!v) return false;
    return /^[A-Z0-9]{2}\d{3,4}$/.test(v);
  }, [normalizeFlightNo]);

  const autoFormatTime = useCallback((raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }, []);

  useEffect(() => {
    if (mode !== 'inline' && !open) return;
    setTab('editor');
    setLoading(false);
    setConvertLoading(false);
    setInfoBlocks([]);
    setCssText('');
    setItineraryHtml('');
    setError('');
    setHeadLinksHtml('');
    setStructuredItinerary(null);
    setJsonLineRoles({});
    setPreviewMode('json');
  }, [open, mode]);

  useEffect(() => {
    const handler = (e) => {
      const data = e.data || {};
      if (data?.type === 'SE_READY') setSeReady(true);
      if (data?.type === 'SE_GET_RESULT' && seGetResolverRef.current) {
        const html = data?.html || '';
        seGetResolverRef.current({ html });
        seGetResolverRef.current = null;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const defaultInfoContentTypeCode = useMemo(() => {
    const list = contentTypes || [];
    const found =
      list.find((t) => String(t.코드명 || '').includes('판매정보')) ||
      list.find((t) => String(t.코드명 || '').includes('투어소개'));
    return found?.코드 || '';
  }, [contentTypes]);

  const getEditorHtml = useCallback(() => {
    return new Promise((resolve) => {
      const w = iframeRef.current?.contentWindow;
      if (!w) return resolve({ html: '' });
      seGetResolverRef.current = resolve;
      try {
        w.postMessage({ type: 'SE_GET' }, '*');
      } catch {
        resolve({ html: '' });
      }
      // safety timeout to avoid hanging if editor doesn't respond
      const timer = setTimeout(() => {
        if (seGetResolverRef.current) {
          seGetResolverRef.current = null;
          resolve({ html: '' });
        }
      }, 5000);
      // if resolved earlier, clear timeout in resolver wrapper
      const originalResolver = seGetResolverRef.current;
      seGetResolverRef.current = (payload) => {
        clearTimeout(timer);
        originalResolver && originalResolver(payload);
      };
    });
  }, []);

  const handleConvert = useCallback(async () => {
    if (!seReady) { alert('에디터가 아직 준비되지 않았습니다.'); return; }
    setConvertLoading(true);
    setError('');
    try {
      const { html } = await getEditorHtml();
      // Remove local file:// image sources that browsers block inside iframes, which can stall editor scripts
      const sanitizedHtml = String(html || '').replace(/src\s*=\s*"file:[^"]*"/gi, '');
      const simplifiedHtml = simplifyHtmlTable(sanitizedHtml);
      const resp = await fetch('https://revato.mytripbox.co.kr/itinerary/df_cellMerge', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain; charset=UTF-8' },
        body: String(simplifiedHtml || ''),
      });
      if (!resp.ok) throw new Error(`요청 실패 (${resp.status})`);
      const data = await resp.json();
      let returned = String((data && data.html) || '');
      if (returned.includes('\\n') || returned.includes('\\"') || returned.includes('\\/')) {
        returned = returned
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\\"/g, '"')
          .replace(/\\\//g, '/');
      }
      // Pass the entire JSON envelope so the parser can also use numbered key-value pairs when HTML lacks info-section
      const parsed = parseItineraryResponse(data);

      const normalizeTitle = (s) => {
        return String(s ?? '')
          .replace(/<\s*br\s*\/?\s*>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/[\u00A0]/g, ' ')
          .replace(/\s+/g, '')
          .trim()
          .toLowerCase();
      };

      const isDateLikeTitle = (title) => {
        const t = String(title ?? '').trim();
        if (!t) return false;
        const compact = t.replace(/\s+/g, '').replace(/[—–]/g, '-');
        const compactNoParen = compact.replace(/\([^)]*\)/g, '');
        // 01/07, 1/7, 01-07, 1-7, 09.01
        if (/^(0?\d|1[0-2])[\/\-.](0?\d|[12]\d|3[01])$/.test(compactNoParen)) return true;
        // 1월 7일, 01월07일
        if (/^(0?\d|1[0-2])월(0?\d|[12]\d|3[01])일$/.test(compactNoParen)) return true;
        // 25.11월 17일 ~ 12월 29일(월) - 연도.월일 ~ 월일(요일) 형식
        if (/^\d{2,4}\.\d{1,2}월\d{1,2}일~\d{1,2}월\d{1,2}일\([월화수목금토일]\)$/.test(compact)) return true;

        const dateToken = '(?:(?:0?\\d|1[0-2])[\\/\\-.](?:0?\\d|[12]\\d|3[01])|(?:0?\\d|1[0-2])월(?:0?\\d|[12]\\d|3[01])일)';
        const rangeRe = new RegExp(`^${dateToken}[~\\-]+${dateToken}$`);
        if (rangeRe.test(compactNoParen)) return true;
        return false;
      };

      const isPriceLikeTitle = (title) => {
        const t = String(title ?? '').trim();
        if (!t) return false;
        const compact = t.replace(/\s+/g, '');
        // Only-price titles: 400,000원 / 400000원 / 400,000 / 400000
        if (/^\d{1,3}(,\d{3})+(원)?$/.test(compact)) return true;
        if (/^\d{5,}(원)?$/.test(compact)) return true;

        // Price tokens inside text: 평수기400,000원 준성수기500,000원...
        // Accept comma-separated groups, or 5+ digits without commas, optionally followed by '원'
        const tokenRe = /(?:\d{1,3}(?:,\d{3})+|\d{5,})원?/g;
        if (tokenRe.test(compact)) return true;
        return false;
      };

      const numberedKeyValues = (parsed && parsed.numberedKeyValues && typeof parsed.numberedKeyValues === 'object')
        ? parsed.numberedKeyValues
        : {};

      const kvByTitle = (() => {
        const out = {};
        try {
          Object.keys(numberedKeyValues).forEach((k) => {
            const raw = String(numberedKeyValues[k] ?? '').trim();
            if (!raw) return;
            const title = raw.split(/\|{2,}/g)[0] || '';
            const tKey = normalizeTitle(title);
            if (!tKey) return;
            if (!Object.prototype.hasOwnProperty.call(out, tKey)) {
              out[tKey] = { kvKey: String(k), rawValue: raw };
            }
          });
        } catch { }
        return out;
      })();

      const initialBlocks = (parsed.infoBlocks || []).map((b) => {
        const title = b.title;
        const key = String(title || '').trim();
        const existing = key ? existingContentByTitle[key] : null;
        let bodyText = b.bodyText || '';
        if (existing) {
          const candidate = (() => {
            const keys = ['태그', '테그', 'html', 'HTML', '내용', 'content', 'body', 'desc', 'description'];
            for (const k of keys) {
              if (Object.prototype.hasOwnProperty.call(existing, k)) {
                return existing[k];
              }
            }
            return '';
          })();
          if (candidate) {
            bodyText = htmlToMultilineText(candidate);
          }
        }
        const inferred = (() => {
          const kvKey = b.kvKey || (kvByTitle[normalizeTitle(title)]?.kvKey);
          const rawValue = b.rawValue || (kvByTitle[normalizeTitle(title)]?.rawValue);
          const hasSource = !!kvKey && !!rawValue;
          if (hasSource && (isDateLikeTitle(title) || isPriceLikeTitle(title))) {
            return PRICE_STOCK_CONTENT_TYPE_CODE;
          }
          return defaultInfoContentTypeCode || '';
        })();

        return {
          id: b.id,
          title,
          bodyText,
          contentTypeCode: inferred,
          rawValue: b.rawValue || (kvByTitle[normalizeTitle(title)]?.rawValue),
          kvKey: b.kvKey || (kvByTitle[normalizeTitle(title)]?.kvKey),
        };
      });
      setCssText(parsed.cssText || '');
      setHeadLinksHtml(parsed.headLinksHtml || '');
      setInfoBlocks(initialBlocks);
      setItineraryHtml(parsed.itineraryHtml || '');
      if (parsed.subData && Array.isArray(parsed.subData.itinerary)) {
        const it = parsed.subData.itinerary;
        const sanitizedIt = (() => {
          try {
            return (it || []).map((dayBlock) => {
              const day = stripHtmlToText(dayBlock?.day);
              const meals = Array.isArray(dayBlock?.meals) ? dayBlock.meals.map((m) => stripHtmlToText(m)) : [];
              const locations = Array.isArray(dayBlock?.locations) ? dayBlock.locations : [];
              return {
                ...dayBlock,
                day,
                meals,
                locations: locations.map((loc) => {
                  const schedule = Array.isArray(loc?.schedule) ? loc.schedule : [];
                  return {
                    ...loc,
                    place: stripHtmlToText(loc?.place),
                    schedule: schedule.map((sch) => {
                      const details = Array.isArray(sch?.details) ? sch.details.map((d) => stripHtmlToText(d)) : [];
                      return {
                        ...sch,
                        time: stripHtmlToText(sch?.time),
                        flight: stripHtmlToText(sch?.flight),
                        details,
                      };
                    }),
                  };
                }),
              };
            });
          } catch {
            return it;
          }
        })();

        setStructuredItinerary(sanitizedIt);
        // 기본 역할 매핑: place -> title, details -> content
        const initialRoles = {};
        try {
          sanitizedIt.forEach((dayBlock, dIdx) => {
            const locs = Array.isArray(dayBlock?.locations) ? dayBlock.locations : [];
            locs.forEach((loc, lIdx) => {
              const placeKey = `place-${dIdx}-${lIdx}`;
              initialRoles[placeKey] = 'title';
              const schedules = Array.isArray(loc?.schedule) ? loc.schedule : [];
              schedules.forEach((sch, sIdx) => {
                const detailsArr = Array.isArray(sch?.details) ? sch.details : [];
                detailsArr.forEach((_, dtIdx) => {
                  const key = `d-${dIdx}-${lIdx}-${sIdx}-${dtIdx}`;
                  initialRoles[key] = 'content';
                });
              });
            });
          });
        } catch { }
        setJsonLineRoles(initialRoles);
      } else {
        setStructuredItinerary(null);
        setJsonLineRoles({});
      }
      setTab('review');
    } catch (e) {
      console.error(e);
      setError('일정표 전환 중 오류가 발생했습니다.');
    } finally {
      setConvertLoading(false);
    }
  }, [seReady, getEditorHtml, existingContentByTitle, htmlToMultilineText, stripHtmlToText, defaultInfoContentTypeCode]);

  const itineraryTypeCode = useMemo(() => {
    const found = (contentTypes || []).find((t) => String(t.코드명 || '').includes(defaultItineraryContentTypeName));
    return found?.코드 || '';
  }, [contentTypes, defaultItineraryContentTypeName]);

  const handleChangeBlock = (idx, field, value) => {
    const next = [...infoBlocks];
    next[idx] = { ...next[idx], [field]: value };
    setInfoBlocks(next);
  };

  const handleDeleteBlock = (idx) => {
    setInfoBlocks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleChangeJsonLineRole = (key, role) => {
    setJsonLineRoles((prev) => ({ ...prev, [key]: role }));
  };

  const ROLE_THEME = {
    title: { border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700', optionColor: '#1d4ed8', optionBg: '#eff6ff', fontWeight: 700 },
    content: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', optionColor: '#047857', optionBg: '#ecfdf3', fontWeight: 600 },
    hotel: { border: 'border-purple-200', bg: 'bg-purple-50', text: 'text-purple-700', optionColor: '#6b21a8', optionBg: '#f5f3ff', fontWeight: 700 },
    ignore: { border: 'border-gray-200', bg: 'bg-gray-100', text: 'text-gray-500', optionColor: '#6b7280', optionBg: '#f3f4f6', fontWeight: 500 },
  };

  const getRoleSelectClass = (role) => {
    const theme = ROLE_THEME[role] || ROLE_THEME.content;
    return `border rounded px-1 py-0.5 text-[11px] bg-white ${theme.border} ${theme.bg} ${theme.text} font-semibold`;
  };

  const getRoleOptionStyle = (role) => {
    const theme = ROLE_THEME[role] || {};
    return {
      color: theme.optionColor || '#374151',
      backgroundColor: theme.optionBg || 'inherit',
      fontWeight: theme.fontWeight || 500,
    };
  };

  const handleRegister = useCallback(async () => {
    if (!productNo) { alert('상품 정보가 없습니다.'); return; }
    const { companyNo, userNo } = getUserInfo() || {};
    if (!companyNo || !userNo) { alert('사용자 정보가 없습니다.'); return; }

    const missing = infoBlocks.filter((b) => !b.contentTypeCode).length;
    if (missing > 0) { alert(`구역이 지정되지 않은 항목이 ${missing}개 있습니다.`); return; }

    const priceStockBlocks = infoBlocks.filter((b) => b.contentTypeCode === PRICE_STOCK_CONTENT_TYPE_CODE);
    const infoBlocksToSave = infoBlocks.filter((b) => b.contentTypeCode !== PRICE_STOCK_CONTENT_TYPE_CODE);

    if (priceStockBlocks.length > 0) {
      const missingKv = priceStockBlocks.filter((b) => !b?.kvKey || !b?.rawValue).length;
      if (missingKv > 0) {
        alert('가격/재고로 선택된 항목 중 원본(df_cellMerge 숫자키) 데이터가 없는 항목이 있습니다.\n해당 항목은 가격/재고 반영이 불가합니다.');
        return;
      }
    }

    const priceStockNumMap = (() => {
      const out = {};
      (priceStockBlocks || []).forEach((b, idx) => {
        const k = String(b?.kvKey || idx + 1);
        const v = String(b?.rawValue || '').trim();
        if (v) out[k] = v;
      });
      return out;
    })();

    // HTML 미리보기 모드: 기존 동작 유지 (상세정보 + 일정소개 HTML 저장)
    if (previewMode === 'html') {
      if (!itineraryHtml) { alert('일정표 영역이 없습니다. 변환을 먼저 실행하세요.'); return; }
      if (!itineraryTypeCode) { alert('일정소개 구역 코드를 찾을 수 없습니다.'); return; }

      setLoading(true);
      try {
        // Save info blocks (text-only: preserve line breaks, no styles)
        for (const b of infoBlocksToSave) {
          const created = await createNewProductContent({
            productNo,
            companyNo,
            userNo,
            contentTypeCode: b.contentTypeCode,
            title: b.title || '콘텐츠',
          });
          let contentNo = null;
          try {
            if (created && Array.isArray(created.table) && created.table[0] && created.table[0].컨텐츠번호) {
              contentNo = Number(created.table[0].컨텐츠번호);
            } else if (created && created.Table && Array.isArray(created.Table) && created.Table[0] && (created.Table[0].컨텐츠번호 || created.Table[0].contentNo)) {
              contentNo = Number(created.Table[0].컨텐츠번호 || created.Table[0].contentNo);
            }
          } catch { }
          if (!contentNo) throw new Error('컨텐츠 생성 실패');
          const text = String(b.bodyText || '').trim();
          const html = text ? text.replace(/\n/g, '<br/>') : '';
          await saveProductContentInfo({
            productNo,
            contentNo,
            companyNo,
            userNo,
            title: b.title || '콘텐츠',
            text,
            html,
            expose: true,
            dealerOnly: false,
            imageMappingUse: true,
            fileAttach: false,
          });
        }

        // Save itinerary section into itinerary content type
        const itineraryCombinedHtml = `${headLinksHtml || ''}${cssText ? `<style>${cssText}</style>` : ''}${itineraryHtml}`;
        const createdIt = await createNewProductContent({
          productNo,
          companyNo,
          userNo,
          contentTypeCode: itineraryTypeCode,
          title: '일정표',
        });
        let itineraryContentNo = null;
        try {
          if (createdIt && Array.isArray(createdIt.table) && createdIt.table[0] && createdIt.table[0].컨텐츠번호) {
            itineraryContentNo = Number(createdIt.table[0].컨텐츠번호);
          } else if (createdIt && createdIt.Table && Array.isArray(createdIt.Table) && createdIt.Table[0] && (createdIt.Table[0].컨텐츠번호 || createdIt.Table[0].contentNo)) {
            itineraryContentNo = Number(createdIt.Table[0].컨텐츠번호 || createdIt.Table[0].contentNo);
          }
        } catch { }
        if (!itineraryContentNo) throw new Error('일정소개 컨텐츠 생성 실패');
        const itineraryText = itineraryCombinedHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        await saveProductContentInfo({
          productNo,
          contentNo: itineraryContentNo,
          companyNo,
          userNo,
          title: '일정표',
          text: itineraryText,
          html: itineraryCombinedHtml,
          expose: true,
          dealerOnly: false,
          imageMappingUse: true,
          fileAttach: false,
        });

        onCompleted && onCompleted({ priceStockNumMap });
        onClose && onClose();
        alert('일정 및 상세정보가 저장되었습니다.');
      } catch (e) {
        console.error(e);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // JSON 파싱 미리보기 모드: 일정소개 HTML 저장은 건너뛰고, 일정표 설정용 일정 카드로 저장
    if (!structuredItinerary || !Array.isArray(structuredItinerary) || structuredItinerary.length === 0) {
      alert('JSON 파싱된 일정 데이터가 없습니다. 일정표 전환을 다시 실행해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 1) 상세정보 블록은 동일하게 저장
      for (const b of infoBlocksToSave) {
        const created = await createNewProductContent({
          productNo,
          companyNo,
          userNo,
          contentTypeCode: b.contentTypeCode,
          title: b.title || '콘텐츠',
        });
        let contentNo = null;
        try {
          if (created && Array.isArray(created.table) && created.table[0] && created.table[0].컨텐츠번호) {
            contentNo = Number(created.table[0].컨텐츠번호);
          } else if (created && created.Table && Array.isArray(created.Table) && created.Table[0] && (created.Table[0].컨텐츠번호 || created.Table[0].contentNo)) {
            contentNo = Number(created.Table[0].컨텐츠번호 || created.Table[0].contentNo);
          }
        } catch { }
        if (!contentNo) throw new Error('컨텐츠 생성 실패');
        const text = String(b.bodyText || '').trim();
        const html = text ? text.replace(/\n/g, '<br/>') : '';
        await saveProductContentInfo({
          productNo,
          contentNo,
          companyNo,
          userNo,
          title: b.title || '콘텐츠',
          text,
          html,
          expose: true,
          dealerOnly: false,
          imageMappingUse: true,
          fileAttach: false,
        });
      }

      // 2) 상품의 일정타입번호 조회
      const typeResp = await getItineraryType({ productNo });
      const itineraryTypeNo = Array.isArray(typeResp?.table) && typeResp.table.length ? typeResp.table[0]?.일정타입번호 : null;
      if (!itineraryTypeNo) {
        throw new Error('일정타입번호를 찾을 수 없습니다.');
      }

      // 3) JSON 일정 데이터 기반으로 일차/장소별 일정 카드 저장
      const getDayNumber = (dayLabel, index) => {
        if (dayLabel) {
          const m = String(dayLabel).match(/\d+/);
          if (m) return Number(m[0]);
        }
        return index + 1;
      };

      // 일차별로 직전에 생성된 일정카드 번호를 기억하여, 다음 카드가 항상 그 뒤에 붙도록 앵커로 사용
      const lastScheduleNoByDay = {};
      // 호텔 정보를 일차별로 수집 (일정 저장 완료 후 일괄 저장)
      const pendingHotels = {}; // { dayNo: [hotelName1, hotelName2, ...] }

      const pendingFlights = [];
      const flightCandidates = [];
      const uniqueFlightNos = new Set();
      const flightSeenByDay = new Set();

      for (let dIdx = 0; dIdx < structuredItinerary.length; dIdx += 1) {
        const dayBlock = structuredItinerary[dIdx];
        const dayNo = getDayNumber(dayBlock?.day, dIdx);
        const locations = Array.isArray(dayBlock?.locations) ? dayBlock.locations : [];
        if (!locations.length) continue;

        // 한 일차(day) 안에서는 여러 장소(locations)를 하나의 연속된 타임라인으로 보고,
        // 제목을 내리거나 올렸을 때 카드가 자연스럽게 분리/통합되도록 day 단위로 상태를 유지한다.
        let currentTitle = '';
        let currentDetails = [];

        const flushCurrentCardIfNeeded = async () => {
          if (!currentTitle) return;
          const detailLines = currentDetails.slice();
          const contentText = detailLines.join('\n\n');
          const selectedScheduleNo = lastScheduleNoByDay[dayNo] || '';
          const addDs = await addItinerarySchedule({
            productNo,
            itineraryTypeNo,
            day: dayNo,
            productTypeCode: undefined,
            name: currentTitle,
            selectedScheduleNo,
          });
          const newNo = Number(addDs?.table?.[0]?.상품일정번호 || 0);
          if (!newNo) throw new Error('신규 일정 번호를 가져오지 못했습니다.');
          lastScheduleNoByDay[dayNo] = newNo;
          await saveItineraryContent({
            scheduleNo: newNo,
            itineraryTypeNo,
            productNo,
            name: currentTitle,
            content: contentText,
            userNo,
          });
          currentTitle = '';
          currentDetails = [];
        };

        for (let lIdx = 0; lIdx < locations.length; lIdx += 1) {
          const loc = locations[lIdx];
          const placeName = stripHtmlToText(loc?.place);
          if (!placeName) continue;

          const placeKey = `place-${dIdx}-${lIdx}`;
          const placeRole = jsonLineRoles[placeKey] || 'title';

          // 1) place 라인 처리
          if (placeRole === 'title') {
            // 이전 카드가 있다면 먼저 저장하고 새 제목 시작
            await flushCurrentCardIfNeeded();
            currentTitle = placeName;
          } else if (placeRole === 'content') {
            // 제목이 아직 없으면 place를 제목으로 사용, 있으면 내용으로 내려쓰기
            if (!currentTitle) {
              currentTitle = placeName;
            } else {
              currentDetails.push(placeName);
            }
          } else if (placeRole === 'hotel') {
            // 호텔 역할: 나중에 일괄 저장하기 위해 수집
            if (!pendingHotels[dayNo]) {
              pendingHotels[dayNo] = [];
            }
            pendingHotels[dayNo].push(placeName);
          } // ignore 는 아무 것도 하지 않음

          // 2) details 배열을 순회하면서 역할에 따라 카드 구성
          const schedules = Array.isArray(loc?.schedule) ? loc.schedule : [];
          for (let sIdx = 0; sIdx < schedules.length; sIdx += 1) {
            const sch = schedules[sIdx];
            const detailsArr = Array.isArray(sch?.details) ? sch.details : [];
            if (!detailsArr.length) continue;
            const time = stripHtmlToText(sch?.time);
            const flight = stripHtmlToText(sch?.flight);
            const normalizedFlight = extractFlightNo(flight);
            if (normalizedFlight && isFlightNoLike(normalizedFlight)) {
              const fKey = `${dayNo}-${normalizedFlight}`;
              if (!flightSeenByDay.has(fKey)) {
                flightSeenByDay.add(fKey);
                uniqueFlightNos.add(normalizedFlight);
                flightCandidates.push({
                  dayNo,
                  flightNo: normalizedFlight,
                  time,
                });
              }
            }
            const prefix = (() => {
              if (time && flight) return `${time} ${flight}`;
              if (time) return time;
              if (flight) return flight;
              return '';
            })();

            for (let dtIdx = 0; dtIdx < detailsArr.length; dtIdx += 1) {
              const line = detailsArr[dtIdx];
              const body = stripHtmlToText(line);
              if (!body) continue;
              const fullLine = prefix ? `${prefix} ${body}` : body;
              const lineKey = `d-${dIdx}-${lIdx}-${sIdx}-${dtIdx}`;
              const role = jsonLineRoles[lineKey] || 'content';

              if (role === 'ignore') {
                continue;
              }
              if (role === 'title') {
                // 지금까지의 카드가 있으면 먼저 저장한 뒤, 새로운 제목으로 시작
                await flushCurrentCardIfNeeded();
                currentTitle = fullLine;
                currentDetails = [];
                continue;
              }
              if (role === 'hotel') {
                // 호텔 역할: 나중에 일괄 저장하기 위해 수집
                if (!pendingHotels[dayNo]) {
                  pendingHotels[dayNo] = [];
                }
                pendingHotels[dayNo].push(fullLine);
                continue;
              }
              // content
              if (!currentTitle) {
                // 제목이 없는 상태에서 content가 나타나면 place를 자동 제목으로 사용
                currentTitle = placeName || body;
              }
              currentDetails.push(fullLine);
            }
          }
        }

        // 이 일차(day)에 대한 마지막 카드가 남아 있으면 저장
        await flushCurrentCardIfNeeded();

        // meals 정보 저장 (조식, 중식, 석식)
        const meals = Array.isArray(dayBlock?.meals) ? dayBlock.meals : [];
        const mealTypes = ['조식', '중식', '석식'];

        for (let mealIdx = 0; mealIdx < Math.min(3, mealTypes.length); mealIdx++) {
          const mealType = mealTypes[mealIdx];
          const mealContent = String(meals[mealIdx] || '').trim();

          if (mealContent) {
            try {
              await saveItineraryMeal({
                productNo,
                itineraryTypeNo,
                dayNo,
                planLabel: mealType,
                oldContent: '', // 기존 내용 없음
                newContent: mealContent
              });
            } catch (e) {
              console.error(`${dayNo}일차 ${mealType} 저장 실패:`, e);
            }
          }
        }
      }

      if (uniqueFlightNos.size > 0) {
        const flightCacheByNo = {};
        try {
          await Promise.all(Array.from(uniqueFlightNos).map(async (flightNo) => {
            try {
              flightCacheByNo[flightNo] = await getFlightScheduleByFlightNo({ flightNo });
            } catch {
              flightCacheByNo[flightNo] = null;
            }
          }));
        } catch { }

        for (const c of flightCandidates) {
          try {
            const ds = flightCacheByNo[c.flightNo];
            const rows = Array.isArray(ds?.table) ? ds.table : [];
            const first = rows && rows.length ? rows[0] : null;
            const depCode = String(first?.출발공항 || '').trim().toUpperCase();
            const arrCode = String(first?.도착공항 || '').trim().toUpperCase();
            const depTimeRaw = String(first?.출발시간 || '').trim();
            const arrTimeRaw = String(first?.도착시간 || '').trim();
            const depTime = depTimeRaw ? autoFormatTime(depTimeRaw) : (c.time ? autoFormatTime(c.time) : '');
            const arrTime = arrTimeRaw ? autoFormatTime(arrTimeRaw) : '';
            if (depCode && arrCode) {
              pendingFlights.push({
                일차: Number(c.dayNo),
                순서: 0,
                편명: c.flightNo,
                출발공항: depCode,
                도착공항: arrCode,
                구분: '217001',
                출발시간: depTime,
                도착시간: arrTime,
                소요시간: '',
              });
            }
          } catch { }
        }
      }

      let itineraryDetailData = null;
      if (pendingFlights.length > 0) {
        let existingFlightItinerary = [];
        try {
          itineraryDetailData = await getItineraryDetail({ productNo, itineraryTypeNo });
          const tables = ['table', 'table1', 'table2', 'table3', 'table4', 'table5'];
          for (const tableName of tables) {
            const table = itineraryDetailData?.[tableName];
            if (!Array.isArray(table) || table.length === 0) continue;
            const firstRow = table[0];
            if (!firstRow || !firstRow.항공여정) continue;
            try {
              const parsed = JSON.parse(firstRow.항공여정);
              if (Array.isArray(parsed)) {
                existingFlightItinerary = parsed.map((item, index) => ({
                  일차: item.일차 || 1,
                  순서: item.순서 || (index + 1),
                  편명: item.편명 || '',
                  출발공항: item.출발공항코드 || item.출발공항 || '',
                  도착공항: item.도착공항코드 || item.도착공항 || '',
                  구분: item.구분 || '217001',
                  출발시간: item.출발시간 || '',
                  도착시간: item.도착시간 || '',
                  소요시간: item.소요시간 || '',
                }));
                break;
              }
            } catch { }
          }
        } catch {
          itineraryDetailData = null;
          existingFlightItinerary = [];
        }

        const maxSeqByDay = {};
        existingFlightItinerary.forEach((x) => {
          const d = Number(x.일차) || 1;
          const s = Number(x.순서) || 0;
          maxSeqByDay[d] = Math.max(Number(maxSeqByDay[d] || 0), s);
        });

        const findMatchIdx = (arr, flightData) => {
          const d = Number(flightData.일차);
          const fno = String(flightData.편명 || '').trim().toUpperCase();
          const dep = String(flightData.출발공항 || '').trim().toUpperCase();
          const arrCode = String(flightData.도착공항 || '').trim().toUpperCase();
          return arr.findIndex((x) => Number(x.일차) === d && (
            (String(x.편명 || '').trim().toUpperCase() === fno && !!fno) ||
            (String(x.출발공항 || '').trim().toUpperCase() === dep && String(x.도착공항 || '').trim().toUpperCase() === arrCode && !!dep && !!arrCode)
          ));
        };

        for (const nf of pendingFlights) {
          const idx = findMatchIdx(existingFlightItinerary, nf);
          if (idx >= 0) {
            const keepSeq = existingFlightItinerary[idx].순서 || (idx + 1);
            existingFlightItinerary[idx] = { ...existingFlightItinerary[idx], ...nf, 순서: keepSeq };
          } else {
            const d = Number(nf.일차) || 1;
            const nextSeq = Number(maxSeqByDay[d] || 0) + 1;
            maxSeqByDay[d] = nextSeq;
            existingFlightItinerary.push({ ...nf, 순서: nextSeq });
          }
        }

        existingFlightItinerary.sort((a, b) => {
          const d = Number(a.일차) - Number(b.일차);
          return d !== 0 ? d : Number(a.순서) - Number(b.순서);
        });

        try {
          await saveFlightItinerary({
            productNo,
            itineraryTypeNo,
            flightItinerary: existingFlightItinerary,
          });
        } catch { }
      }

      // 수집된 호텔 정보를 일괄 저장 (일정 등록 완료 후)
      // 모든 요금제(1인1실, 2인1실, 3인1실 등)에 동일한 호텔을 등록한다.
      const hotelDays = Object.keys(pendingHotels);
      if (hotelDays.length > 0) {
        // 1) 상품의 전체 요금제 목록을 자원(resource) 단위로 조회
        // 호텔의 요금제는 보통 인원수 옵션(1인1실/2인1실/...)으로 구성되어 있으며,
        // 한 호텔 자원에 여러 요금제가 묶여 있다.
        const allPlanEntries = []; // [{ planNo, planName, resourceNo }]
        try {
          const resources = await getTourProductResourceList({ companyNo, productNo });
          const resourceList = Array.isArray(resources) ? resources : [];
          // 자원별로 요금제를 병렬 조회 (부분 실패는 무시)
          const planResults = await Promise.all(
            resourceList.map(async (r) => {
              const resourceNo = r?.자원번호;
              if (!resourceNo) return [];
              try {
                const planRows = await getTourRatePlanList02({ companyNo, productNo, resourceNo });
                const rows = Array.isArray(planRows) ? planRows : [];
                return rows
                  .map(pl => ({
                    planNo: pl?.가격옵션번호,
                    planName: (typeof pl?.요금제명 === 'string' ? pl.요금제명.trim() : '') || String(pl?.가격옵션번호 ?? ''),
                    resourceNo,
                  }))
                  .filter(p => p.planNo != null && String(p.planNo).trim() !== '');
              } catch (e) {
                console.warn(`자원 ${resourceNo} 요금제 조회 실패`, e);
                return [];
              }
            })
          );
          planResults.forEach(arr => arr.forEach(p => allPlanEntries.push(p)));
        } catch (e) {
          console.warn('상품 요금제 조회 실패', e);
        }

        // 2) 기존 table5 데이터에서 이미 등록된 (일차, 요금제) 조합의 이전 값(prevPlaceName/prevBigo) 매핑
        // 동일 (일차, 요금제) 행에 대해 SP가 기존 값을 식별하여 갱신할 수 있도록 함
        const prevByDayPlan = {}; // { `${dayNo}-${planNo}`: { prevPlaceName, prevBigo } }
        try {
          const data = itineraryDetailData || await getItineraryDetail({ productNo, itineraryTypeNo });
          const table5 = Array.isArray(data?.table5) ? data.table5 : [];
          table5.forEach(row => {
            const day = Number(row?.일차 || 0);
            const planNo = row?.가격옵션번호 || row?.요금제번호;
            if (day > 0 && planNo != null && String(planNo).trim() !== '') {
              const key = `${day}-${String(planNo)}`;
              prevByDayPlan[key] = {
                prevPlaceName: row?.일정명 || row?.장소명 || '',
                prevBigo: row?.일정내용 || row?.비고 || '',
              };
            }
          });
        } catch (e) {
          console.warn('기존 숙박 정보 조회 실패', e);
        }

        // 3) 각 일차의 호텔을 한 번의 호출로 모든 요금제에 등록
        // SP(@숙박리스트, @요금제상태리스트)가 배열 입력을 지원하므로
        // 일차마다 자원(resource) 단위로 묶어서 모든 요금제를 한 번에 보낸다.
        // 요금제 목록을 조회하지 못한 경우 기본요금제(1) 폴백.
        const fallbackPlans = [{ planNo: '1', planName: '기본요금제', resourceNo: '' }];
        const targetPlans = allPlanEntries.length > 0 ? allPlanEntries : fallbackPlans;

        // 자원번호별로 요금제 그룹핑 (@선택자원번호가 단일 값이므로 자원별로 호출 분리)
        const plansByResource = {};
        targetPlans.forEach((p) => {
          const key = String(p.resourceNo || '');
          if (!plansByResource[key]) plansByResource[key] = [];
          plansByResource[key].push(p);
        });

        for (const dayNoStr of hotelDays) {
          const dayNo = Number(dayNoStr);
          const hotelNames = pendingHotels[dayNoStr];
          // 같은 일차에 여러 호텔이 있으면 합쳐서 저장
          const hotelName = (hotelNames || []).join(', ');
          if (!hotelName) continue;

          for (const resourceKey of Object.keys(plansByResource)) {
            const plansInResource = plansByResource[resourceKey];
            const plansForCall = plansInResource.map((p) => {
              const prevKey = `${dayNo}-${String(p.planNo)}`;
              const prev = prevByDayPlan[prevKey] || {};
              return {
                planNo: p.planNo,
                planName: p.planName || '기본요금제',
                prevPlaceName: prev.prevPlaceName || '',
                prevBigo: prev.prevBigo || '',
              };
            });

            try {
              await saveHotelItineraryEdits({
                productNo,
                itineraryTypeNo,
                dayNo,
                placeNo: null,
                placeName: hotelName,
                bigo: '',
                resourceNo: resourceKey,
                plans: plansForCall,
              });
            } catch (e) {
              const names = plansForCall.map(p => p.planName || p.planNo).join(', ');
              console.error(`${dayNo}일차 호텔 저장 실패 (요금제: ${names}):`, e);
            }
          }
        }
      }

      onCompleted && onCompleted({ priceStockNumMap });
      onClose && onClose();
      alert('일정 및 상세정보가 저장되었습니다.');
    } catch (e) {
      console.error(e);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [productNo, infoBlocks, itineraryHtml, cssText, headLinksHtml, itineraryTypeCode, structuredItinerary, previewMode, jsonLineRoles, stripHtmlToText, onCompleted, onClose, normalizeFlightNo, extractFlightNo, isFlightNoLike, autoFormatTime]);

  // Expose functions to parent via ref
  useImperativeHandle(ref, () => ({
    handleConvert,
    handleRegister,
    getCurrentTab: () => tab,
    isConvertLoading: () => convertLoading,
    isRegisterLoading: () => loading,
    isSeReady: () => seReady,
    hasItineraryHtml: () => !!itineraryHtml,
  }), [handleConvert, handleRegister, tab, convertLoading, loading, seReady, itineraryHtml]);

  if (mode !== 'inline' && !open) return null;

  return (
    <div className={mode === 'inline' ? "w-full" : "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"}>
      <div className={mode === 'inline' ? "bg-white w-full max-w-none flex flex-col" : "bg-white w-full max-w-6xl max-h-[90vh] flex flex-col"}>
        <div className={mode === 'inline' ? "flex items-center justify-between px-4 py-3 border-b bg-gray-50" : "flex items-center justify-between px-6 py-4 border-b"}>
          <div>
            <h2 className="text-lg font-bold text-gray-900">일정 스마트 등록</h2>
            <p className="text-xs text-gray-600 mt-1">한글, 엑셀, 워드 양식의 일정을 전체 복사하여 붙여넣고 전환하여 상세정보와 일정표를 한 번에 저장합니다.</p>
          </div>
          {mode !== 'inline' && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>

        <div
          data-tour="qw-smart-tabs"
          className={mode === 'inline' ? "px-4 pt-3 border-b bg-gray-50 flex items-center gap-4 text-sm" : "px-6 pt-3 border-b bg-gray-50 flex items-center gap-4 text-sm"}
        >
          <button
            type="button"
            className={`px-3 py-2 -mb-px border-b-2 transition-colors ${tab === 'editor'
              ? 'border-blue-600 text-blue-700 font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            onClick={() => setTab('editor')}
            aria-selected={tab === 'editor'}
            role="tab"
          >
            에디터
          </button>
          <button
            type="button"
            className={`px-3 py-2 -mb-px border-b-2 transition-colors ${tab === 'review'
              ? 'border-blue-600 text-blue-700 font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            onClick={() => setTab('review')}
            disabled={infoBlocks.length === 0 && !itineraryHtml}
            aria-selected={tab === 'review'}
            role="tab"
          >
            검토
          </button>
          <div className="ml-auto flex items-center gap-2">
            {tab === 'editor' && (
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-indigo-600 text-white disabled:opacity-50"
                onClick={handleConvert}
                disabled={convertLoading || !seReady}
              >
                {convertLoading ? '변환중...' : '일정표 전환'}
              </button>
            )}
            {tab === 'review' && (
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-50"
                onClick={handleRegister}
                disabled={loading || !itineraryHtml}
              >
                {loading ? '저장중...' : '일정 등록'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {/* Keep the editor iframe mounted to preserve content when switching tabs */}
          <div className="flex-1 min-h-0" style={{ display: tab === 'editor' ? 'block' : 'none' }}>
            <iframe
              ref={iframeRef}
              title="SmartEditor"
              src="/smarteditor/SmartEditor4.html"
              className="w-full"
              style={{ border: 0, height: mode === 'inline' ? '95vh' : '95vh' }}
              onLoad={() => setSeReady(false)}
            />
          </div>

          {tab === 'review' && (
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
              <div className="border border-blue-100 bg-blue-50 rounded-lg p-3">
                <div className="text-sm font-semibold text-blue-900 mb-2">스마트 등록 안내</div>
                <div className="text-xs text-blue-900 space-y-1">
                  <div>1) 불필요한 항목은 <span className="font-semibold">삭제</span>할 수 있습니다.</div>
                  <div>2) <span className="font-semibold">상세정보</span>에서 각 항목의 <span className="font-semibold">구역(노출 위치)</span>을 설정할 수 있습니다.</div>
                  <div>3) 가격 관련 내용은 <span className="font-semibold">구역 선택에서 ‘가격/재고’</span>로 분리하면, 일정 등록 후 <span className="font-semibold">스마트 가격등록</span>으로 이어져 자동 등록(일괄 반영)이 가능합니다.</div>
                  <div>4) <span className="font-semibold">일정표 정보</span>에서 <span className="font-semibold">일정명/일정내용/호텔</span> 구분을 선택할 수 있고, <span className="font-semibold">식사</span>는 자동 분류됩니다. 기본적으로 도시는 일정명 기준으로 자동 분류되며 필요 시 변경할 수 있습니다.</div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">상세정보</h3>
                {infoBlocks.length === 0 ? (
                  <div className="text-xs text-gray-500">감지된 상세정보 블록이 없습니다.</div>
                ) : (
                  <div className="space-y-3">
                    {infoBlocks.map((b, idx) => (
                      <div key={b.id} className="border rounded p-3 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start mb-2">
                          <div className="md:col-span-2 flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs text-gray-500">제목</label>
                              <input type="text" className="px-2 py-1.5 border rounded text-sm" value={b.title} onChange={(e) => handleChangeBlock(idx, 'title', e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs text-gray-500">구역</label>
                              <select
                                className={`px-2 py-1.5 border rounded text-sm ${b.contentTypeCode === PRICE_STOCK_CONTENT_TYPE_CODE ? 'text-red-600 font-semibold' : 'text-gray-900'}`}
                                value={b.contentTypeCode}
                                onChange={(e) => handleChangeBlock(idx, 'contentTypeCode', e.target.value)}
                              >
                                <option value="">-- 구역 선택 --</option>
                                {contentTypes.filter(t => String(t.코드명 || '').trim() !== '일정소개').map((t) => (
                                  <option key={t.코드} value={t.코드}>{t.코드명}</option>
                                ))}
                                <option value="__DIVIDER__" disabled>-- 가격/재고 --</option>
                                <option value={PRICE_STOCK_CONTENT_TYPE_CODE} style={{ color: '#ff0000ff', fontWeight: 600 }}>가격/재고</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex items-start justify-end">
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded border border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
                              onClick={() => handleDeleteBlock(idx)}
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-gray-500">내용(수정가능)</label>
                          <textarea
                            className="w-full border rounded p-2 text-sm font-[system-ui] whitespace-pre-wrap min-h-[100px]"
                            value={b.bodyText || ''}
                            onChange={(e) => handleChangeBlock(idx, 'bodyText', e.target.value)}
                            placeholder="본문 텍스트"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">일정표 정보</h3>
                {!itineraryHtml && !structuredItinerary && (
                  <div className="text-xs text-gray-500">일정표 전환을 실행하면 이곳에 미리보기가 표시됩니다.</div>
                )}

                {(itineraryHtml || structuredItinerary) && (
                  <div>
                    <div className="mb-4 grid grid-cols-2 gap-0 p-1">
                      <button
                        type="button"
                        className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${previewMode === 'json'
                          ? 'bg-white text-emerald-700 shadow-sm border border-emerald-200'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        onClick={() => setPreviewMode('json')}
                      >
                        📋 정식 일정표
                      </button>
                      <button
                        type="button"
                        className={`px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${previewMode === 'html'
                          ? 'bg-white text-blue-700 shadow-sm border border-blue-200'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        onClick={() => setPreviewMode('html')}
                      >
                        📄 HTML약식 일정표
                      </button>
                    </div>

                    {previewMode === 'html' && (
                      <div className="border rounded overflow-hidden">
                        {itineraryHtml ? (
                          <div className="max-h-[480px] overflow-auto">
                            {headLinksHtml ? <span dangerouslySetInnerHTML={{ __html: headLinksHtml }} /> : null}
                            {cssText ? <style>{cssText}</style> : null}
                            <div dangerouslySetInnerHTML={{ __html: itineraryHtml }} />
                          </div>
                        ) : (
                          <div className="p-3 text-xs text-gray-500">HTML 미리보기를 사용할 수 없습니다.</div>
                        )}
                      </div>
                    )}

                    {previewMode === 'json' && (
                      <div className="bg-white h-auto">
                        {structuredItinerary && Array.isArray(structuredItinerary) ? (
                          <div className="space-y-3 text-[14px] text-gray-800">
                            {structuredItinerary.map((dayBlock, dIdx) => (
                              <div key={dIdx} className="border border-gray-200 rounded-md p-2">
                                <div className="font-semibold text-gray-900 mb-1">{dayBlock.day || `Day ${dIdx + 1}`}</div>
                                {Array.isArray(dayBlock.locations) && dayBlock.locations.length > 0 ? (
                                  <div className="space-y-1">
                                    {dayBlock.locations.map((loc, lIdx) => {
                                      const placeKey = `place-${dIdx}-${lIdx}`;
                                      const placeRole = jsonLineRoles[placeKey] || 'title';
                                      const placeTextClass = placeRole === 'title'
                                        ? 'text-[14px] font-semibold text-blue-700'
                                        : placeRole === 'content'
                                          ? 'text-[14px] text-gray-700'
                                          : placeRole === 'hotel'
                                            ? 'text-[14px] font-semibold text-purple-700'
                                            : 'text-[14px] text-gray-400 line-through';
                                      const placeContainerClass = placeRole === 'title'
                                        ? 'border-t border-gray-200 pt-1 mt-1 first:border-t-0 first:pt-0 first:mt-0'
                                        : placeRole === 'hotel'
                                          ? 'border-t border-purple-200 pt-1 mt-1 first:border-t-0 first:pt-0 first:mt-0 bg-purple-50 rounded-md'
                                          : 'pt-0 mt-0 first:pt-0 first:mt-0';
                                      return (
                                        <div key={lIdx} className={placeContainerClass}>
                                          <div className="mb-0.5 flex items-center justify-between gap-2 rounded-md px-1 -mx-1 cursor-pointer hover:bg-gray-50 py-3">
                                            <span className={`truncate max-w-[60%] text-[14px] ${placeTextClass}`}>{loc.place || '장소 미지정'}</span>
                                            <select
                                              className={getRoleSelectClass(placeRole)}
                                              value={placeRole}
                                              onChange={(e) => handleChangeJsonLineRole(placeKey, e.target.value)}
                                            >
                                              <option value="title" style={getRoleOptionStyle('title')}>일정명</option>
                                              <option value="content" style={getRoleOptionStyle('content')}>일정내용</option>
                                              <option value="hotel" style={getRoleOptionStyle('hotel')}>호텔</option>
                                              <option value="ignore" style={getRoleOptionStyle('ignore')}>반영안함</option>
                                            </select>
                                          </div>
                                          {Array.isArray(loc.schedule) && loc.schedule.length > 0 ? (
                                            <ul className="mt-0.5 space-y-3">
                                              {loc.schedule.map((sch, sIdx) => (
                                                <li key={sIdx} className="flex gap-2">
                                                  {(sch.time || sch.flight) && (
                                                    <span className="shrink-0 text-[14px] text-gray-500 min-w-[60px]">
                                                      {sch.time || ''}{sch.time && sch.flight ? ' · ' : ''}{sch.flight || ''}
                                                    </span>
                                                  )}
                                                  <div className="flex-1 space-y-0.5">
                                                    {Array.isArray(sch.details) && sch.details.length > 0 ? (
                                                      sch.details.map((dt, dtIdx) => {
                                                        const lineKey = `d-${dIdx}-${lIdx}-${sIdx}-${dtIdx}`;
                                                        const role = jsonLineRoles[lineKey] || 'content';
                                                        const isTitle = role === 'title';
                                                        const isHotel = role === 'hotel';
                                                        const isIgnored = role === 'ignore';
                                                        const containerClass = `flex items-start justify-between gap-2 rounded-md px-1 -mx-1 cursor-pointer hover:bg-gray-50 ${isTitle ? 'border-t border-gray-200 pt-1 mt-1' : ''} ${isHotel ? 'border-t border-purple-200 pt-1 mt-1 bg-purple-50' : ''} ${isIgnored ? 'opacity-50' : ''}`;
                                                        const textClass = isTitle
                                                          ? 'text-[14px] text-blue-700 font-semibold'
                                                          : isHotel
                                                            ? 'text-[14px] text-purple-700 font-semibold'
                                                            : 'text-[14px] text-gray-700';
                                                        return (
                                                          <div key={dtIdx} className={containerClass}>
                                                            <div className={`${textClass} leading-snug flex-1`}>
                                                              {dt}
                                                            </div>
                                                            <select
                                                              className={`${getRoleSelectClass(role)} shrink-0`}
                                                              value={role}
                                                              onChange={(e) => handleChangeJsonLineRole(lineKey, e.target.value)}
                                                            >
                                                              <option value="title" style={getRoleOptionStyle('title')}>일정명</option>
                                                              <option value="content" style={getRoleOptionStyle('content')}>일정내용</option>
                                                              <option value="hotel" style={getRoleOptionStyle('hotel')}>호텔</option>
                                                              <option value="ignore" style={getRoleOptionStyle('ignore')}>반영안함</option>
                                                            </select>
                                                          </div>
                                                        );
                                                      })
                                                    ) : null}
                                                  </div>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : (
                                            <div className="text-[11px] text-gray-500 mt-0.5">상세 일정이 없습니다.</div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-[11px] text-gray-500">장소 정보가 없습니다.</div>
                                )}

                                {/* meals 정보 표시 */}
                                {Array.isArray(dayBlock.meals) && dayBlock.meals.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <div className="text-[11px] font-semibold text-gray-700 mb-1">식사 정보:</div>
                                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                                      <div className="rounded p-1">
                                        <div className="font-semibold text-orange-700">조식</div>
                                        <div className="text-gray-600">{dayBlock.meals[0] || '미정'}</div>
                                      </div>
                                      <div className="rounded p-1">
                                        <div className="font-semibold text-green-700">중식</div>
                                        <div className="text-gray-600">{dayBlock.meals[1] || '미정'}</div>
                                      </div>
                                      <div className="rounded p-1">
                                        <div className="font-semibold text-blue-700">석식</div>
                                        <div className="text-gray-600">{dayBlock.meals[2] || '미정'}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">JSON 파싱 미리보기를 사용할 수 없습니다.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {error ? (
          <div className={mode === 'inline' ? "px-4 py-3 text-xs text-red-600 border-t" : "px-6 py-3 text-xs text-red-600 border-t"}>{error}</div>
        ) : null}
      </div>
    </div>
  );
});

SmartItineraryRegisterModal.displayName = 'SmartItineraryRegisterModal';

export default SmartItineraryRegisterModal;
